import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	REPORTED_EXCEPTION,
	StaticLookup,
	setPreference,
	getPreference,
	NumberUtils,
} from 'ps-gui';
import {
	parseRoleId,
} from './utils';
import { EMPTY_LOOKUP } from '../data/lookup';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);
const EMPTY_STATE = createModuleState('Document');

const formatAmount = NumberUtils.getFixedDecimalFormatNumber(2);

export default class DocumentModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#getPreferenceKeyBase() {
		return '//elodoc//';
	}

	#getExtFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'extFilter';
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new DocumentModel(this.#data.setIn(name, value));
		} else {
			return new DocumentModel(this.#data.set(name, value));
		}
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		return (await this.initialize(globalContext)).#setProperty('languageTimestamp', new Date().getTime());
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	setSelectedTabId(value) {
		return this.#setProperty('selectedTabId', value);
	}



	setRoleId(roleId) {
		let model = this.#setProperty('roleId', roleId);
		const dp = this.getDistributionPeriodLookup();
		if (!dp.getText(this.#data.get('distributionPeriodId'))) {
			model = model.setDistributionPeriodId(dp.getItems().getIn([0, 'key']));
		}
		return model;
	}

	setDistributionPeriodId(value) {
		return this.#setProperty('distributionPeriodId', value);
	}


	setRoleLookup(roles) {
		return this.#setProperty('roleLookup', StaticLookup(Immutable.fromJS(roles)));
	}

	async initializeLookups() {
		let pi = await webapi.execute('/api/proc/api_elodocuments/getpartyinfo');
		if (pi === REPORTED_EXCEPTION) {
			return this;
		}
		// console.log('pi = ', pi);
		let model = this.setRoleLookup(pi.roles || []);
		model = model.#setProperty('distributionPeriodLookup', StaticLookup(Immutable.fromJS(pi.distributionPeriods || [])));

		if (pi?.roles?.length > 0) {
			return model.setRoleId(pi.roles[0].key);
		} else {
			return model;
		}
	}

	getDistributionPeriodLookup() {
		return this.#data.get('distributionPeriodLookup') || EMPTY_LOOKUP;
	}

	initializePreferences() {
		let data = this.#data;
		let ef = getPreference(this.#getExtFilterPreferenceKey());
		if (!ef) {
			return this;
		}
		const roleLookup = data.get('roleLookup');
		if (roleLookup) {
			const roleId = ef.get('roleId');
			if (roleId && roleLookup.getText(roleId)) {
				data = data.set('roleId', roleId);
			}
		}
		const distLookup = this.getDistributionPeriodLookup();
		if (distLookup) {
			const distPeriodId = ef.get('distributionPeriodId') || distLookup.getItems().getIn([0, 'key']);
			data = data.set('distributionPeriodId', distPeriodId);
		}
		return new DocumentModel(data);

	}

	async initialize(globalContext) {
		let model = await this.initializeLookups();
		model = model.#setProperty('langId', globalContext.getLanguageId());
		model = await model.loadData(true);
		return model;
	}


	async loadData(dontSavePreference) {
		const roleId = this.#data.get('roleId');
		if (!roleId) {
			return this;
		}
		const distributionPeriodId = this.#data.get('distributionPeriodId');
		let filter = parseRoleId(roleId);
		filter.year = parseInt(distributionPeriodId);

		if (!dontSavePreference && roleId && distributionPeriodId) {
			setPreference(this.#getExtFilterPreferenceKey(), Immutable.Map({
				roleId,
				distributionPeriodId
			}));
		}
		const upd = await webapi.execute('/api/elo/checkParty', {
			party_id: filter.partyId
		});
		let res = await webapi.execute('/api/proc/api_elodocuments/getDocuments', filter);

		return this.#setProperty('payments', Immutable.fromJS(res?.payments || [])).#setProperty('invoiceDocuments', Immutable.fromJS(res?.invoiceDocuments || []));
	}


	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

